import { useCallback, useEffect, useRef, useState } from 'react'
import { INSTALL_COMMANDS } from './AgentLauncher'
import { ONE_CMD } from './OneCmd'

export type StepIconName = 'doc' | 'cube' | 'terminal'

export const HANDOFF_STEPS = [
  {
    n: '1',
    icon: 'terminal',
    title: 'Install OpenDeploy in Terminal',
    body: 'Copy the installer command and run it once.',
  },
  {
    n: '2',
    icon: 'cube',
    title: 'Send /opendeploy to your agent',
    body: 'Your agent detects the stack and deploys the current project.',
  },
  {
    n: '3',
    icon: 'terminal',
    title: 'Get URL, bind project',
    body: 'Open the live URL. If it works, bind the project to your account and keep it online.',
  },
] as const

function StepIcon({ name }: { name: StepIconName }) {
  if (name === 'cube') {
    return (
      <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
        <path d="M20 4.5 34 12.2v15.6L20 35.5 6 27.8V12.2L20 4.5Z" />
        <path d="M6 12.2 20 20l14-7.8M20 20v15.5" />
      </svg>
    )
  }

  if (name === 'terminal') {
    return (
      <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
        <rect x="6" y="9" width="28" height="22" rx="2" />
        <path d="m12 17 4 3-4 3M19 25h8" />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <path d="M12 5.5h11l5 5v24H12V5.5Z" />
      <path d="M23 5.5v6h5M16 18h9M16 23h9M16 28h6" />
    </svg>
  )
}

function fallbackCopy(text: string) {
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'
  document.body.appendChild(textArea)
  textArea.select()

  try {
    document.execCommand('copy')
  } finally {
    document.body.removeChild(textArea)
  }
}

function StartDocChip({
  copied,
  onCopy,
}: {
  copied: boolean
  onCopy: () => void
}) {
  return (
    <button
      type="button"
      className={`od-how-control od-how-file-chip od-how-copy-chip${copied ? ' is-copied' : ''}`}
      onClick={onCopy}
      aria-label={copied ? 'Copied OpenDeploy installer command' : 'Copy the OpenDeploy installer command'}
    >
      <StepIcon name="terminal" />
      <span>{copied ? 'copied' : 'copy installer'}</span>
    </button>
  )
}

function CommandChip({
  copied,
  onCopy,
}: {
  copied: boolean
  onCopy: () => void
}) {
  return (
    <button
      type="button"
      className={`od-how-command-chip${copied ? ' is-copied' : ''}`}
      onClick={onCopy}
      aria-label={copied ? `Copied: ${ONE_CMD}` : `Copy the deploy command: ${ONE_CMD}`}
    >
      <code>{ONE_CMD}</code>
      <span>{copied ? 'copied' : 'copy'}</span>
    </button>
  )
}

export default function HandoffSteps({
  sectionId = 'how',
  headingId = 'home-how-title',
  headingLevel = 'h2',
}: {
  sectionId?: string
  headingId?: string
  headingLevel?: 'h1' | 'h2'
}) {
  const Heading = headingLevel
  const sectionRef = useRef<HTMLElement | null>(null)
  const [copiedPrompt, setCopiedPrompt] = useState(false)
  const [copiedCommand, setCopiedCommand] = useState(false)
  const [motionState, setMotionState] = useState({
    observing: false,
    revealed: false,
  })

  useEffect(() => {
    const node = sectionRef.current
    if (!node) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      setMotionState({ observing: false, revealed: true })
      return
    }

    setMotionState({ observing: true, revealed: false })

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setMotionState({ observing: true, revealed: true })
        observer.disconnect()
      },
      {
        rootMargin: '0px 0px -15% 0px',
        threshold: 0.18,
      },
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  const copyInstaller = useCallback(() => {
    const markCopied = () => {
      setCopiedPrompt(true)
      window.setTimeout(() => setCopiedPrompt(false), 1200)
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(INSTALL_COMMANDS[0].command).then(markCopied).catch(() => {
        fallbackCopy(INSTALL_COMMANDS[0].command)
        markCopied()
      })
      return
    }

    fallbackCopy(INSTALL_COMMANDS[0].command)
    markCopied()
  }, [])

  const copyCommand = useCallback(() => {
    const markCopied = () => {
      setCopiedCommand(true)
      window.setTimeout(() => setCopiedCommand(false), 1200)
    }

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(ONE_CMD).then(markCopied).catch(() => {
        fallbackCopy(ONE_CMD)
        markCopied()
      })
      return
    }

    fallbackCopy(ONE_CMD)
    markCopied()
  }, [])

  const sectionClassName = [
    'od-home-how',
    motionState.observing ? 'is-observing' : '',
    motionState.revealed ? 'is-revealed' : '',
  ].filter(Boolean).join(' ')

  return (
    <section ref={sectionRef} id={sectionId} className={sectionClassName} aria-labelledby={headingId}>
      <div className="od-home-how-head">
        <Heading id={headingId}>How it works</Heading>
        <p className="od-home-how-subtitle">
          Install once, send /opendeploy to your agent, then bind the working project.
        </p>
      </div>

      <div className="od-how-track" aria-hidden="true">
        {HANDOFF_STEPS.map((step) => (
          <span key={step.n}>{step.n}</span>
        ))}
      </div>

      <ol className="od-how-grid" aria-label="OpenDeploy handoff flow">
        {HANDOFF_STEPS.map((step) => (
          <li key={step.n} className={`od-how-card od-home-how-card-${step.n}`}>
            <span className="od-how-icon">
              <StepIcon name={step.icon} />
            </span>
            <h3>{step.title}</h3>
            <p>{step.body}</p>

            {step.n === '1' && (
              <div className="od-how-file-list" aria-label="Installer handoff">
                <StartDocChip copied={copiedPrompt} onCopy={copyInstaller} />
              </div>
            )}

            {step.n === '2' && (
              <div className="od-how-check-list" aria-label="Deploy command">
                <CommandChip copied={copiedCommand} onCopy={copyCommand} />
              </div>
            )}

            {step.n === '3' && (
              <>
                <div className="od-how-control od-how-command" aria-label="Live URL example">
                  <code>your-app.opendeploy.site</code>
                  <button type="button" className="od-how-bind-button">
                    ready to bind
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ol>
    </section>
  )
}
